import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { Brand } from 'src/brands/brand';
import { environment } from 'src/environments/environment';

export type UiTextKind = 'Recommendation' | 'ResultText' | 'HistoryText' | 'ChromeLabel';

export interface UiTextDto {
  key: string;
  heading: string;
  text: string;
  linkText: string;
  linkUrl: string;
  linkType: string;
  imageUrl: string;
  imageAlt: string;
  tags: string;
  kind: UiTextKind;
  contentItemId: string;
}

export interface RiskRecommendationDto {
  risk: number;
  keys: string[];
}

export interface DialogDto {
  metadata?: {
    dbName?: string;
    jsonName?: string;
    version?: number;
    nextButtonDelay?: number | string;
    fadeInDelay?: number | string;
    fadeOutDelay?: number | string;
    [key: string]: unknown;
  };
  activeLanguage?: string;
  // topNode and everything beneath it is walked exclusively by the legacy
  // jQuery dialog runtime (run_bmi / componentLibrary/*) — left untyped.
  [key: string]: unknown;
}

/**
 * Reads bulk UI copy (recommendations, result/history text, chrome labels) from
 * SI.PreventorBackend's `/api/preventor/texts` endpoint. One Orchard tenant per brand,
 * so no `brand` param — Brand.cmsMockUrl/cmsProdUrl already point at the right tenant.
 */
@Injectable({
  providedIn: 'root'
})
export class CmsContentService {

  private cache = new Map<string, Observable<UiTextDto[]>>();
  private riskRecommendationsCache?: Observable<RiskRecommendationDto[]>;
  private dialogCache = new Map<string, Observable<DialogDto | undefined>>();
  private reported = new Set<string>();

  constructor(private http: HttpClient) { }

  private get baseUrl(): string {
    return environment.mockBackend ? Brand.cmsMockUrl : Brand.cmsProdUrl;
  }

  private get apiKey(): string {
    return environment.mockBackend ? Brand.cmsMockApiKey : Brand.cmsProdApiKey;
  }

  private get headers(): HttpHeaders {
    return new HttpHeaders({ 'X-API-Key': this.apiKey });
  }

  /** Orchard returns media paths relative to its own tenant (e.g. "/media/foo.svg"); resolve them against the CMS base URL. Absolute URLs are left as-is. */
  private resolveImageUrl(imageUrl: string): string {
    if (!imageUrl || /^https?:\/\//i.test(imageUrl)) {
      return imageUrl;
    }

    return `${this.baseUrl}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
  }

  /** Bulk-fetches and caches (per culture+kind) for the lifetime of the session. */
  getAll(culture: string, kind?: UiTextKind): Observable<UiTextDto[]> {
    const cacheKey = `${culture}|${kind ?? ''}`;
    let cached = this.cache.get(cacheKey);

    if (!cached) {
      let params = new HttpParams().set('culture', culture);
      if (kind) {
        params = params.set('kind', kind);
      }

      cached = this.http.get<UiTextDto[]>(`${this.baseUrl}/api/preventor/texts`, { headers: this.headers, params }).pipe(
        map(items => items.map(item => ({ ...item, imageUrl: this.resolveImageUrl(item.imageUrl) }))),
        catchError(error => {
          console.error('Failed to load CMS texts', error);
          return of([] as UiTextDto[]);
        }),
        shareReplay(1)
      );

      this.cache.set(cacheKey, cached);
    }

    return cached;
  }

  /** Bulk-fetches and caches the risk -> recommendation-keys mapping for the lifetime of the session. Not culture-specific. */
  getRiskRecommendations(): Observable<RiskRecommendationDto[]> {
    if (!this.riskRecommendationsCache) {
      this.riskRecommendationsCache = this.http.get<RiskRecommendationDto[]>(`${this.baseUrl}/api/preventor/risk-recommendations`, { headers: this.headers }).pipe(
        catchError(error => {
          console.error('Failed to load CMS risk-recommendations mapping', error);
          return of([] as RiskRecommendationDto[]);
        }),
        shareReplay(1)
      );
    }

    return this.riskRecommendationsCache;
  }

  /** Resolves a category result icon (BMI/gamtest risk-group image, e.g. "green.svg") against the CMS media library. */
  getCategoryImageUrl(category: string, subcategory: string, image: string): string {
    return `${this.baseUrl}/media/preventor/categories/${category}/${subcategory}/${image}`;
  }

  /** Resolves a total-risk meter image (e.g. "yellow-2.svg") against the CMS media library. */
  getRiskLevelMeterImageUrl(image: string): string {
    return `${this.baseUrl}/media/preventor/risk-level-meter/large/${image}`;
  }

  /** Fetches and caches a single dialog document by key for the lifetime of the session. */
  getDialog(key: string): Observable<DialogDto | undefined> {
    let cached = this.dialogCache.get(key);

    if (!cached) {
      cached = this.http.get<DialogDto>(`${this.baseUrl}/api/preventor/dialogs/${encodeURIComponent(key)}`, { headers: this.headers }).pipe(
        catchError(error => {
          console.error(`Failed to load CMS dialog '${key}'`, error);
          return of(undefined);
        }),
        shareReplay(1)
      );

      this.dialogCache.set(key, cached);
    }

    return cached;
  }

  /** Looks up a single key out of the cached bulk fetch, reporting a gap if it's missing. */
  getByKey(key: string, culture: string, kind?: UiTextKind): Observable<UiTextDto | undefined> {
    return this.getAll(culture, kind).pipe(
      tap(items => {
        if (!items.some(i => i.key === key)) {
          this.reportMissing(key, culture);
        }
      }),
      map(items => items.find(i => i.key === key))
    );
  }

  /** Fire-and-forget report of a key/culture the frontend expected but didn't get. Safe to call speculatively. Reports each key/culture pair once per session. */
  reportMissing(key: string, culture: string): void {
    const reportKey = `${key}|${culture}`;
    if (this.reported.has(reportKey)) {
      return;
    }
    this.reported.add(reportKey);

    this.http.post(`${this.baseUrl}/api/preventor/texts/missing`, { key, culture }, { headers: this.headers }).pipe(
      catchError(() => of(null))
    ).subscribe();
  }
}
